import { openAccountDialog } from "./controller.ts";

interface ConfirmOptions { title: string; message: string; confirmLabel: string; danger?: boolean }
interface TextOptions extends ConfirmOptions { label: string; inputType?: "text" | "email" | "password" }

export interface AccountWorkflowCapabilities {
  email: () => string;
  pendingChanges: () => Promise<number>;
  selectedTaskId: () => string | null;
  leaveTaskPresence: (taskId: string) => Promise<void>;
  request: (path: string, init?: RequestInit) => Promise<Response | null>;
  apiError: (response: Response, fallback: string) => Promise<string>;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  text: (options: TextOptions) => Promise<string | null>;
  markSignedOut: () => Promise<void>;
  resetSession: () => void;
  closeNavigation: () => void;
  renderSignedOut: (message: string) => void;
  clearLocalData: () => Promise<void>;
  reload: () => void;
  icon: (name: string) => string;
  formatTimestamp: (value: string) => string;
  toast: (message: string, error?: boolean) => void;
}

export function createAccountWorkflow(capabilities: AccountWorkflowCapabilities) {
  async function beforeLogout(): Promise<void> {
    const taskId = capabilities.selectedTaskId();
    if (taskId) await capabilities.leaveTaskPresence(taskId);
  }

  async function signedOut(): Promise<void> {
    await capabilities.markSignedOut();
    capabilities.resetSession();
    capabilities.closeNavigation();
    capabilities.renderSignedOut("Signed out. Local data remains on this device.");
  }

  async function open(): Promise<void> {
    openAccountDialog({
      email: capabilities.email(),
      pendingChanges: await capabilities.pendingChanges(),
      request: capabilities.request,
      apiError: capabilities.apiError,
      confirm: capabilities.confirm,
      text: capabilities.text,
      beforeLogout,
      signedOut,
      accountDeleted: capabilities.markSignedOut,
      clearLocalData: capabilities.clearLocalData,
      reload: capabilities.reload,
      icon: capabilities.icon,
      formatTimestamp: capabilities.formatTimestamp,
      toast: capabilities.toast,
    });
  }

  return { open, beforeLogout, signedOut };
}
